import { Reducer } from 'redux'
import { CloudProvidersType } from '../../../admin/modules/NewCustomers/reducer'
import { MappingManagerTemplateStatusType } from '../../../oppsync/modules/coSellMappingManager/reducer'
import { MarketplaceMappingManagerActionTypes } from './action'
import { Product, ProductDimension } from '../productsListing/reducer'

export interface ProductsWithDimensions {
  productId: string
  productName: string
  cloudMarketplace: CloudProvidersType
  productDimensions: ProductDimension[]
}

export interface MarketplaceTemplatesDetailsData {
  templateName: string
  templateId: string
  templateType: string
  status:
    | MappingManagerTemplateStatusType.DRAFT
    | MappingManagerTemplateStatusType.ACTIVE
  createdBy: string
  creationDate: string
  lastUpdatedAt: string
}

export interface MarketplaceFieldMappingPicklistData {
  referralValue: string
  hubspotValue: string
  hubspotLabel: string
}

export interface MarketplaceFieldMapping {
  referralField: string
  referralFieldType: string
  hubspotObject: string
  hubspotField: string
  hubspotFieldLabel: string
  hubspotFieldType: string
  isHidden: boolean
  isReadonly: boolean
  isMapped: boolean
  label: string
  required: boolean
  displayOrder: number
  defaultValue: string | string[]
  category: string
  picklist: MarketplaceHubspotFieldsOptions[] | null
  picklistMappings: MarketplaceFieldMappingPicklistData[] | null
}

export interface MarketplaceFieldMappingDimensions
  extends MarketplaceFieldMapping {
  dimensionId?: string
  crmFieldLabel?: string
  crmFieldType?: string
  crmField?: string
}

export interface MarketplaceFieldMappingInstallments
  extends MarketplaceFieldMapping {
  crmFieldLabel?: string
  crmFieldType?: string
  crmField?: string
}

export interface MarketplaceFieldMappingMetadata {
  crmFieldLabel?: string
  crmFieldType?: string
  crmField?: string
  helpText?: string
  overwrite?: boolean
  maxLength: number | null
  minLength: number | null
  description?: string
  placeholder?: string
  key?: string
  referralField: string
  referralFieldType: string
  isHidden: boolean
  isReadonly: boolean
  isMapped: boolean
  label: string
  required: boolean
  displayOrder: number
  defaultValue: string | string[]
  category: string
  picklist: MarketplaceHubspotFieldsOptions[] | null
  picklistMappings: MarketplaceFieldMappingPicklistData[] | null
}

export interface MarketplaceFieldMappingRecipientSource {
  crmFieldLabel?: string
  crmFieldType?: string
  crmField?: string
  helpText?: string
  maxLength: number | null
  minLength: number | null
  description?: string
  placeholder?: string
  referralField: string
  referralFieldType: string
  isHidden: boolean
  isReadonly: boolean
  isMapped: boolean
  label: string
  required: boolean
  displayOrder: number
  defaultValue: string | string[]
  category: string
  picklist: MarketplaceHubspotFieldsOptions[] | null
  picklistMappings: MarketplaceFieldMappingPicklistData[] | null
}

export interface MarketplaceTemplatesDataFieldMappings {
  fields?: {
    fieldsMapping?: {
      [key: string]: MarketplaceFieldMapping
    }
  }
  awsAccountNumber?: MarketplaceFieldMapping
  companyName?: MarketplaceFieldMapping
  recipient?: MarketplaceFieldMapping
  customEmailMessage?: MarketplaceFieldMapping
  state?: MarketplaceFieldMapping
  contractDuration?: MarketplaceFieldMapping
  entitlementOption?: MarketplaceFieldMapping
  renewal?: MarketplaceFieldMapping
  eula?: MarketplaceFieldMapping
  customEula?: MarketplaceFieldMapping
  dimensions?: {
    dimensionsMapping: {
      [key: string]: {
        dimensions: {
          [key: string]: MarketplaceFieldMappingDimensions
        }
        productName: string
      }
    }
  }
  installments?: {
    installmentsMapping?: {
      [key: string]: MarketplaceFieldMappingInstallments
    }
  }
  metadata?: {
    metadataMapping?: {
      [key: string]: MarketplaceFieldMappingMetadata
    }
  }
  recipientSource?: {
    recipientSourceMapping?: {
      [key: string]: MarketplaceFieldMappingRecipientSource
    }
  }
}

export interface MarketplaceTemplateDataResponse {
  id: string
  name: string
  templateType: string
  autoReferralCreation: boolean
  autoReferralDealTypeTrigger: string
  cloudProvider: CloudProvidersType
  isDraft: boolean
  isActive: boolean
  fieldMappings: MarketplaceTemplatesDataFieldMappings
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
  useTemplateFromMappingService?: boolean
}

export interface MarketplaceTemplateData {
  templateData: MarketplaceTemplateDataResponse
}

export interface MarketplaceHubspotFieldsOptions {
  value: string
  label: string
}

export interface MarketplaceHubspotFieldsData {
  internalName: string
  displayLabel: string
  fieldType: string
  objectType: string
  description: string
  options?: MarketplaceHubspotFieldsOptions[]
}

export interface MarketplaceHubspotFieldsDataResponse {
  fields: MarketplaceHubspotFieldsData[]
}

export interface MarketplaceSalesforceFieldsData {
  internalName: string
  displayLabel: string
  fieldType: string
  objectType: string
  description: string
}

export interface MarketplaceSalesforceFieldsDataResponse {
  fields: MarketplaceSalesforceFieldsData[]
}

export interface MarketplaceMappingManagerData {
  templateDetails: MarketplaceTemplatesDetailsData[]
  templateData: MarketplaceTemplateData
  hubspotFields: MarketplaceHubspotFieldsDataResponse
  salesforceFields: MarketplaceSalesforceFieldsDataResponse
  productsWithDimensions: ProductsWithDimensions[]
}

const initialState: MarketplaceMappingManagerData = {
  templateDetails: [] as MarketplaceTemplatesDetailsData[],
  templateData: {} as MarketplaceTemplateData,
  hubspotFields: {} as MarketplaceHubspotFieldsDataResponse,
  salesforceFields: {} as MarketplaceSalesforceFieldsDataResponse,
  productsWithDimensions: [] as ProductsWithDimensions[],
}

type actionType =
  | {
      type: MarketplaceMappingManagerActionTypes.SET_TEMPLATE_DATA_INFO
      payload: MarketplaceTemplatesDetailsData[]
    }
  | {
      type: MarketplaceMappingManagerActionTypes.SET_TEMPLATE_DATA
      payload: MarketplaceTemplateDataResponse
    }
  | {
      type: MarketplaceMappingManagerActionTypes.SET_TEMPLATE_AS_DRAFT
      payload: boolean
    }
  | {
      type: MarketplaceMappingManagerActionTypes.SET_HUBSPOT_FIELDS
      payload: MarketplaceHubspotFieldsData[]
    }
  | {
      type: MarketplaceMappingManagerActionTypes.SET_SALESFORCE_FIELDS
      payload: MarketplaceSalesforceFieldsData[]
    }
  | {
      type: MarketplaceMappingManagerActionTypes.UPDATE_TEMPLATE_SECTION_FIELD
      payload: {
        fieldKey: keyof MarketplaceTemplatesDataFieldMappings
        updates: Partial<MarketplaceFieldMapping>
      }
    }
  | {
      type: MarketplaceMappingManagerActionTypes.SET_PRODUCTS_WITH_DIMENSIONS
      payload: { products: ProductsWithDimensions[] }
    }

export const reducer: Reducer<MarketplaceMappingManagerData, actionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case MarketplaceMappingManagerActionTypes.SET_TEMPLATE_DATA_INFO: {
      return {
        ...state,
        templateDetails: action.payload,
      }
    }
    case MarketplaceMappingManagerActionTypes.SET_TEMPLATE_DATA: {
      return {
        ...state,
        templateData: {
          templateData: action.payload,
        },
      }
    }
    case MarketplaceMappingManagerActionTypes.SET_TEMPLATE_AS_DRAFT: {
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: {
            ...state.templateData.templateData,
            isDraft: action.payload,
            isActive: !action.payload,
          },
        },
      }
    }
    case MarketplaceMappingManagerActionTypes.SET_HUBSPOT_FIELDS: {
      return {
        ...state,
        hubspotFields: {
          fields: action.payload,
        },
      }
    }
    case MarketplaceMappingManagerActionTypes.SET_SALESFORCE_FIELDS: {
      return {
        ...state,
        salesforceFields: {
          fields: action.payload,
        },
      }
    }
    case MarketplaceMappingManagerActionTypes.UPDATE_TEMPLATE_SECTION_FIELD: {
      const { fieldKey, updates } = action.payload
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: {
            ...state.templateData.templateData,
            fieldMappings: {
              ...state.templateData.templateData.fieldMappings,
              [fieldKey]: {
                ...(state.templateData.templateData.fieldMappings?.[fieldKey] ??
                  {}),
                ...updates,
              },
            },
          },
        },
      }
    }
    case MarketplaceMappingManagerActionTypes.SET_PRODUCTS_WITH_DIMENSIONS: {
      return {
        ...state,
        productsWithDimensions: action.payload?.products,
      }
    }
    default: {
      return state
    }
  }
}
